import { useState, useEffect } from "react";
import { useUiContextStore } from "../../stores/uiContextStore";
import { useNavigate, useParams } from "react-router";
import {
  CheckCircle2,
  XCircle,
  Loader2,
  MessageSquare,
  Clock,
  AlertCircle,
  ShieldCheck,
  Info,
  FileText,
  Eye,
  X,
  Download,
  Check,
  Zap,
  Search,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Link2,
  Calendar,
  CalendarCheck,
} from "lucide-react";
import { PageShell } from "../components/layout/PageShell";
import { Breadcrumb } from "../components/Breadcrumb";
import { Modal, ModalShell } from "../components/Modal";
import { DataTable, type Column } from "../components/DataTable";
import { Dropdown } from "../components/Dropdown";
import { AutomationChecksView } from "../components/AutomationChecksView";
import {
  FilePreviewBody,
  FilePreviewHeader,
} from "../components/FilePreview";
import { useToastStore } from "../../stores/toastStore";
import { RBACAction } from "../components/RBACButton";
import {
  getAssessmentPlan,
  getAssessmentExecutionView,
  getLinkedEvidence,
  previewSupportingEvidenceFile,
  downloadSupportingEvidenceFile,
  reviewAssessmentPlanStatement,
  validateControlsWithLlm,
  generatePoam,
  getAuthHeaders,
  type EvidenceLink,
  type PendingControl,
  type ExecutionReviewItem,
  type ApiAssessmentPlan,
  type ControlExecutionStatusResponse,
  type AssessmentExecutionViewResponse,
  type AssessmentExecutionViewStatement,
  type AssessmentExecutionViewIteration,
  type LlmControlValidationResult,
} from "../../api/frameworksApi";
import { mapExecutionViewToStatuses, normalizeExecutionStatus } from "./AssessmentExecution";
import { FollowUpChatModal } from "../components/FollowUpChatModal";
import { FollowUpPanel, FollowUpAction, type FollowUpControlType } from "../components/FollowUpPanel";

/* ── Types ── */

interface ReviewAction {
  status: "accepted" | "approved" | "follow_up_raised" | "rejected" | null;
  followUpQuestion: string;
  reviewComments: string;
  needsEvidence: boolean;
}

interface ReviewRow {
  item: ExecutionReviewItem;
  ctrl: PendingControl | undefined;
  stmtInfo:
    | {
        statementId: string;
        statementDescription: string;
        assessmentMethods: string[];
      }
    | undefined;
  idx: number;
  kind: "declarative";
}

interface AutoReviewRow {
  autoCtrl: ControlExecutionStatusResponse;
  idx: number;
  kind: "automation";
}

type TableRow = ReviewRow | AutoReviewRow;

/* ── Helpers ── */
const countWords = (text: string) =>
  text.trim().split(/\s+/).filter(Boolean).length;
const MIN_WORDS = 20;

const normalizeReviewVerdict = (status?: string | null) => {
  const value = (status ?? "").toLowerCase();
  if (value.includes("pass")) return "passed";
  if (value.includes("fail")) return "failed";
  if (value.includes("pending")) return "pending";
  return value || "pending";
};

const getSavedActionStatus = (
  history?: { reviewStatus?: string | null; followUpQuestion?: string | null; followUpResponse?: string | null },
): ReviewAction["status"] => {
  if (!history) return null;
  if (history.reviewStatus === "rejected" || history.followUpResponse === "rejected") return "rejected";
  if (
    history.reviewStatus === "follow_up_raised" ||
    history.reviewStatus === "follow_up_resolved" ||
    history.followUpQuestion
  ) {
    return "follow_up_raised";
  }
  if (history.reviewStatus === "accepted" || history.reviewStatus === "approved") {
    return "accepted";
  }
  return null;
};

const stateToReviewStatus = (
  statement: AssessmentExecutionViewStatement,
): string => {
  // Reviewability is gated by `state`, not `review_status`. The backend
  // returns ASMT-0051-E "Statement not reviewable" if state === "FOLLOWUP"
  // (the SO hasn't responded yet). Only AWAITING_REVIEW and
  // FOLLOWUP_RESOLVED are reviewable. `review_status` is a historical
  // attribute of the latest iteration and must NOT be used to short-circuit
  // state here — doing so previously caused Approve/Reject to surface 409s.
  const state = (statement.state ?? "NEW").toUpperCase();
  if (state === "AWAITING_REVIEW") return "pending";
  if (state === "FOLLOWUP_RESOLVED") return "follow_up_resolved";
  if (state === "FOLLOWUP") return "follow_up_raised";
  if (state === "REVIEWED") return statement.review_status ?? "accepted";
  return "new";
};

const mapExecutionViewToReviewItems = (
  view: AssessmentExecutionViewResponse,
): ExecutionReviewItem[] =>
  (view.controls ?? []).flatMap((control) =>
    (control.statements ?? []).map((statement) => {
      const currentReviewStatus = stateToReviewStatus(statement);
      // Build reviewHistory from iterations[] when available. One entry per
      // round; SO's response in round N is the NEXT iteration's justification.
      // Fall back to the synthetic top-level entry for backends that don't
      // return iterations.
      const iters = statement.iterations ?? [];
      const reviewHistory =
        iters.length > 0
          ? iters.map((iter, i) => {
              const next = iters[i + 1];
              return {
                id: `iter-${iter.iteration_number}-${statement.statement_id}`,
                reviewStatus: iter.review_status ?? "",
                followUpQuestion: iter.reviewer_note ?? null,
                followUpResponse: next?.justification ?? null,
                followUpRespondedBy: null,
                followUpRespondedAt: next?.saved_at ?? null,
                reviewedBy: "",
                reviewedAt: iter.reviewed_at ?? iter.saved_at ?? "",
              };
            })
          : statement.note || statement.review_status || statement.reviewed_at
          ? [
              {
                id: statement.statement_execution_id ?? statement.statement_id,
                reviewStatus: statement.review_status ?? currentReviewStatus,
                followUpQuestion: statement.note ?? null,
                followUpResponse: null,
                followUpRespondedBy: null,
                followUpRespondedAt: null,
                reviewedBy: statement.reviewed_by ?? "",
                reviewedAt: statement.reviewed_at ?? "",
              },
            ]
          : [];

      return {
        execution: {
          id: statement.statement_id,
          planControlId: control.plan_control_id,
          controlId: control.control_id,
          controlDescription: control.control_description,
          statementId: statement.statement_id,
          statementLabel: statement.statement_label,
          statementDescription: statement.statement_description,
          executionMethod:
            statement.execution_method ??
            (control.mode?.toUpperCase() === "AUTOMATED" ? "opa" : "declarative"),
          determination: normalizeReviewVerdict(statement.verdict) === "passed",
          justification: statement.justification ?? "",
          executedBy: "",
          executedAt: "",
          reviewStatus:
            currentReviewStatus === "new" ? "pending" : (currentReviewStatus as any),
          editable: currentReviewStatus !== "accepted" && currentReviewStatus !== "rejected",
          followUpQuestion: statement.note ?? null,
          createdAt: "",
          updatedAt: "",
        },
        currentReviewStatus,
        reviewHistory,
      };
    }),
  );

const mapExecutionViewToPendingControls = (
  view: AssessmentExecutionViewResponse,
): PendingControl[] =>
  (view.controls ?? []).map((control) => ({
    id: control.plan_control_id,
    controlId: control.control_id,
    controlDescription: control.control_description,
    statements: (control.statements ?? []).map((statement, idx) => ({
      id: statement.statement_id,
      statementId: statement.statement_id,
      statementDescription: statement.statement_description,
      assessmentMethods: statement.needs_evidence
        ? ["declarative_evidence", ...(statement.assessment_methods ?? [])]
        : (statement.assessment_methods ?? []),
      sortOrder: idx,
      createdAt: "",
      updatedAt: "",
      executionMethod: statement.execution_method ?? null,
      state: statement.state,
      justification: statement.justification ?? null,
      iterations: statement.iterations ?? [],
      evidenceFiles: statement.evidence_files ?? [],
    })),
  }));

/* For an automated re-save the system owner's reply is stored as the statement's
 * justification (not as a followUpResponse in reviewHistory). When the history has
 * no owner response yet but the statement is resolved with a justification, append
 * it as a response entry so the chat shows both sides. */
const buildChatHistory = (
  base: ExecutionReviewItem["reviewHistory"],
  exec: ExecutionReviewItem["execution"] | undefined,
  status: string | undefined,
): ExecutionReviewItem["reviewHistory"] => {
  if (!exec) return base;
  const sentinels = new Set(["accepted", "rejected", "approved"]);
  const hasOwnerResponse = base.some(
    (h) => h.followUpResponse && !sentinels.has(h.followUpResponse.trim().toLowerCase()),
  );
  const justification = exec.justification?.trim();
  if (hasOwnerResponse || !justification || status !== "follow_up_resolved") return base;
  return [
    ...base,
    {
      id: `${exec.id}-owner-resp`,
      reviewStatus: "follow_up_resolved",
      followUpQuestion: null,
      followUpResponse: justification,
      followUpRespondedBy: null,
      followUpRespondedAt: exec.updatedAt || exec.executedAt || exec.createdAt || new Date().toISOString(),
      reviewedBy: "",
      reviewedAt: exec.updatedAt || "",
    },
  ];
};

/* ── Component ── */

export function AssessmentReview() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [generatingPoam, setGeneratingPoam] = useState(false);
  // Shown after a successful generate — generating finalizes the plan and
  // removes it from the Review & Execution pages (only viewable in Results).
  // The popup lets the user pick where to go next.
  const [showPoamDone, setShowPoamDone] = useState(false);
  const handleGeneratePoam = async () => {
    if (!id) return;
    setGeneratingPoam(true);
    try {
      const res = await generatePoam(id);
      const d = res.data;
      useToastStore.getState().showSuccess(
        "POA&M Updated",
        `${d.created} created · ${d.updated} updated · ${d.closed} closed`,
      );
      // Plan is now finalized — ask the user where to go next.
      setShowPoamDone(true);
    } catch (err: any) {
      const apiMsg: string = err?.message || "";
      let msg = apiMsg || "Failed to generate POA&M.";
      if (/not found|404/i.test(apiMsg)) msg = "Assessment plan not found.";
      else if (/ssp/i.test(apiMsg)) msg = "This plan has no resolvable SSP.";
      else if (/frozen|locked/i.test(apiMsg))
        msg = "The POA&M document is locked and can't be updated.";
      useToastStore.getState().showError("Error", msg);
    }
    setGeneratingPoam(false);
  };

  const [controls, setControls] = useState<PendingControl[]>([]);
  const [reviewItems, setReviewItems] = useState<ExecutionReviewItem[]>([]);
  const [planInfo, setPlanInfo] = useState<ApiAssessmentPlan | null>(null);
  // Publish the plan under review, so {item} questions in assessment.yaml apply. Cleared on unmount.
  useEffect(() => {
    const title = planInfo?.title;
    useUiContextStore
      .getState()
      .setSelectedItem(id && title ? { id, name: title } : null);
    return () => useUiContextStore.getState().clearSelectedItem();
  }, [id, planInfo]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Actions per execution ID
  const [actions, setActions] = useState<Record<string, ReviewAction>>({});
  const [savedActions, setSavedActions] = useState<Set<string>>(new Set());
  const [savingDraft, setSavingDraft] = useState(false);
  const [savingAuto, setSavingAuto] = useState(false);
  const [tablePage, setTablePage] = useState(0);
  const [tablePageSize, setTablePageSize] = useState(10);
  const [tableSearch, setTableSearch] = useState('');
  const [tableKindFilter, setTableKindFilter] = useState<'all' | 'not_yet_assessed' | 'declarative' | 'declarative_evidence' | 'automation'>('all');
  const [tableStatusFilter, setTableStatusFilter] = useState<'all' | 'not_reviewed' | 'not_assessed' | 'not_satisfied' | 'satisfied'>('all');
  const [tableSortKey, setTableSortKey] = useState<string | null>(null);
  const [tableSortDir, setTableSortDir] = useState<'asc' | 'desc'>('asc');
  const handleTableSort = (key: string) => {
    if (tableSortKey === key) setTableSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setTableSortKey(key); setTableSortDir('asc'); }
    setTablePage(0);
  };

  /* ── Review modal state ── */
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewModalRow, setReviewModalRow] = useState<ReviewRow | null>(null);
  const [showExecutionResponse, setShowExecutionResponse] = useState(false);

  // Default the system owner's response (justification + evidence) to "open" when
  // a row's review modal is opened — for D/D+E it carries the actual response,
  // so it should be visible upfront. Close button still collapses it.
  useEffect(() => {
    if (reviewModalOpen) setShowExecutionResponse(true);
  }, [reviewModalOpen, reviewModalRow]);

  /* ── Evidence per statement ── */
  const [evidenceMap, setEvidenceMap] = useState<
    Record<string, EvidenceLink[]>
  >({});
  /* Full follow-up conversation history per statement, from the review API
   * (execution-view only carries the latest note, not the full Q&A). */
  const [reviewHistoryMap, setReviewHistoryMap] = useState<
    Record<string, ExecutionReviewItem["reviewHistory"]>
  >({});


  /* ── Evidence preview modal ──
     The shared preview engine (components/FilePreview) fetches the file, sniffs
     its real type and renders PDFs, images, text and Word documents inline. */
  const [previewEvidenceId, setPreviewEvidenceId] = useState<string | null>(
    null,
  );
  const [previewTitle, setPreviewTitle] = useState("");

  /* ── Chat modal state ── */
  const [chatOpen, setChatOpen] = useState(false);
  const [chatItem, setChatItem] = useState<ExecutionReviewItem | null>(null);
  const [chatControlId, setChatControlId] = useState("");

  useEffect(() => {
    setShowExecutionResponse(false);
  }, [reviewModalRow?.item.execution.id]);

  const openChat = (item: ExecutionReviewItem, controlId: string) => {
    setChatItem(item);
    setChatControlId(controlId);
    setChatOpen(true);
  };

  const resolveSspTitle = (_href: string | undefined): string | null => {
    return planInfo?.sspTitle ?? null;
  };

  /* ── Fetch data ── */
  const fetchData = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      // Review page is driven entirely by execution-view (plus plan detail +
      // linked evidence). The review API is no longer called — execution-view's
      // statement.state / note / justification carry everything we render.
      const [planRes, evidenceRes, viewRes] = await Promise.all([
        getAssessmentPlan(id).catch(() => ({
          data: null as ApiAssessmentPlan | null,
        })),
        getLinkedEvidence(id, "assessment_execution").catch(() => ({
          data: [] as EvidenceLink[],
        })),
        getAssessmentExecutionView(id),
      ]);

      const view = viewRes.data;
      setControls(view ? mapExecutionViewToPendingControls(view) : (planRes.data?.reviewedControls ?? []));
      setPlanInfo(planRes.data);
      const mappedItems = view ? mapExecutionViewToReviewItems(view) : [];
      setReviewItems(mappedItems);
      setReviewHistoryMap({});
      // Server-driven overlay: `review_complete` from execution-view drives the
      // "All Controls Completed" UI.
      setServerAllReviewed(Boolean(view?.review_complete));
      const evidenceLinks = evidenceRes.data ?? [];
      const nextEvidenceMap: Record<string, EvidenceLink[]> = {};
      for (const link of evidenceLinks) {
        if (!nextEvidenceMap[link.statementId]) nextEvidenceMap[link.statementId] = [];
        nextEvidenceMap[link.statementId].push(link);
      }
      for (const control of view?.controls ?? []) {
        for (const statement of control.statements ?? []) {
          for (const file of statement.evidence_files ?? []) {
            const existing = nextEvidenceMap[statement.statement_id] ?? [];
            if (existing.some((link) => link.supportingEvidenceId === file.supporting_evidence_id)) {
              continue;
            }
            nextEvidenceMap[statement.statement_id] = [
              ...existing,
              {
                id: file.supporting_evidence_id,
                assessmentPlanId: view.plan_id,
                statementId: statement.statement_id,
                supportingEvidenceId: file.supporting_evidence_id,
                evidenceName: file.title || file.file_name,
                linkedBy: "",
                createdAt: "",
              },
            ];
          }
        }
      }
      setEvidenceMap(nextEvidenceMap);

      // Implementation status comes from the plan detail API (reviewedControls).
      const implByControl = new Map<string, string>();
      for (const rc of planRes.data?.reviewedControls ?? []) {
        if (rc.implementationStatus) {
          implByControl.set(rc.controlId, rc.implementationStatus);
        }
      }

      // Map execution-view → unified control statuses (automated + manual)
      const mapped = view ? mapExecutionViewToStatuses(view) : [];
      const deduped = mapped.map((ctrl) => {
        const implementationStatus =
          implByControl.get(ctrl.controlId) ?? ctrl.implementationStatus;
        if (!ctrl.opaResults?.length) return { ...ctrl, implementationStatus };
        const seen = new Set<string>();
        return {
          ...ctrl,
          implementationStatus,
          opaResults: ctrl.opaResults.filter((opa) => {
            const key = `${opa.opaControlId}_${opa.assetUuid ?? ''}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          }),
        };
      });
      setControlStatuses(deduped);
    } catch (err) {
      console.error("Failed to load review data:", err);
      setError("Failed to load review data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    if (error) useToastStore.getState().showError("Error", error);
  }, [error]);

  /* ── Evidence preview handler ──
     Opening the modal is all that's needed: FilePreviewBody does the
     authenticated fetch, type detection and rendering. */
  const handleEvidencePreview = (evidenceId: string, title: string) => {
    setPreviewTitle(title);
    setPreviewEvidenceId(evidenceId);
  };

  const closePreview = () => {
    setPreviewEvidenceId(null);
    setPreviewTitle("");
  };

  const handleEvidenceDownload = async (evidenceId: string, fileName?: string) => {
    try {
      const url = downloadSupportingEvidenceFile(evidenceId);
      const res = await fetch(url, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('File not available');
      const contentType = res.headers.get('content-type') || '';
      if (contentType.includes('application/xml') || contentType.includes('text/xml')) {
        throw new Error('File not available');
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = fileName || `evidence-${evidenceId}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Download failed:', err);
      useToastStore.getState().showError("Error", 'File is not available for this evidence.');
    }
  };

  /* ── Automation state ── */
  const [controlStatuses, setControlStatuses] = useState<ControlExecutionStatusResponse[]>([]);
  const [autoModalOpen, setAutoModalOpen] = useState(false);
  const [evidencePage, setEvidencePage] = useState(0);
  const [autoOpaPage, setAutoOpaPage] = useState(0);
  // Mirror pagination for the review modal's Automation Results block.
  // Keeps the popup compact (2 OPA results per page) so long evidence
  // lists don't blow up the modal height.
  const [reviewOpaPage, setReviewOpaPage] = useState(0);
  const AUTO_OPA_PER_PAGE = 3;
  const [autoModalCtrl, setAutoModalCtrl] = useState<ControlExecutionStatusResponse | null>(null);
  // Whether the system owner's re-submitted response (justification + evidence)
  // is expanded in the automation review modal.
  const [autoRespViewOpen, setAutoRespViewOpen] = useState(false);
  const [autoActions, setAutoActions] = useState<Record<string, ReviewAction>>({});
  const [savedAutoActions, setSavedAutoActions] = useState<Set<string>>(new Set());

  /* Automation Results modal: per-check filter / search / page size — handles
   * controls with many automated checks (hundreds of asset × tech rows). */
  const [autoChecksFilter, setAutoChecksFilter] = useState<"all" | "passed" | "failed" | "pending">("all");
  const [autoChecksSearch, setAutoChecksSearch] = useState("");
  const [autoRowsPerPage, setAutoRowsPerPage] = useState(25);
  useEffect(() => {
    setAutoOpaPage(0);
    setAutoChecksFilter("all");
    setAutoChecksSearch("");
  }, [autoModalCtrl?.planControlId]);

  /* Persist automation review state across refreshes. Automation rows don't
   * map cleanly to a single statement review action, so localStorage keyed by
   * plan id is the fallback. */
  useEffect(() => {
    if (!id) return;
    try {
      const raw = localStorage.getItem(`assessment_review_auto_${id}`);
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        actions?: Record<string, ReviewAction>;
        saved?: string[];
      };
      if (parsed.actions) setAutoActions(parsed.actions);
      if (parsed.saved) setSavedAutoActions(new Set(parsed.saved));
    } catch {
      // ignore — corrupt entry, start fresh
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    try {
      localStorage.setItem(
        `assessment_review_auto_${id}`,
        JSON.stringify({ actions: autoActions, saved: Array.from(savedAutoActions) }),
      );
    } catch {
      // quota / privacy mode — silently drop persistence
    }
  }, [id, autoActions, savedAutoActions]);

  /* ── LLM Validation state ── */
  const [llmValidating, setLlmValidating] = useState(false);
  const [llmResults, setLlmResults] = useState<LlmControlValidationResult[] | null>(null);
  const [llmModalOpen, setLlmModalOpen] = useState(false);


  const [serverAllReviewed, setServerAllReviewed] = useState(false);

  const isAutomationMethod = (executionMethod?: string | null) => executionMethod === "opa";

  const automationVerdictAction = (
    controlId: string,
    determination?: boolean | null,
  ): "accept" | "reject" => {
    const detail = controlStatuses.find((s) => s.controlId === controlId);
    if (detail?.opaOverallStatus) return detail.opaOverallStatus === "passed" ? "accept" : "reject";
    return determination === true ? "accept" : "reject";
  };

  const updateAction = (execId: string, updates: Partial<ReviewAction>) => {
    setActions((prev) => ({
      ...prev,
      [execId]: {
        ...(prev[execId] ?? {
          status: null,
          followUpQuestion: "",
          needsEvidence: false,
        }),
        ...updates,
      },
    }));
  };

  const handleSaveAction = async (execId: string) => {
    if (!id) return;
    const action = actions[execId];
    if (!action?.status) return;
    const item = reviewItems.find((ri) => ri.execution.id === execId);
    if (!item) return;

    const payload =
      action.status === "follow_up_raised"
        ? {
            action: "followup" as const,
            needs_evidence: Boolean(action.needsEvidence),
            note: action.followUpQuestion.trim(),
          }
        : isAutomationMethod(item.execution.executionMethod)
          ? {
              action: automationVerdictAction(
                item.execution.controlId,
                item.execution.determination,
              ),
            }
          : {
              action: action.status === "rejected" ? "reject" as const : "accept" as const,
            };

    if (action.status === "follow_up_raised") {
      if (!action.followUpQuestion.trim()) {
        useToastStore.getState().showError("Follow-Up Note Required", "Please enter a follow-up note before saving.");
        return;
      }
    }

    setSavingDraft(true);
    try {
      await reviewAssessmentPlanStatement(id, item.execution.statementId, payload);
      setActions((prev) => {
        const next = { ...prev };
        delete next[execId];
        return next;
      });
      setSavedActions((prev) => {
        const next = new Set(prev);
        next.delete(execId);
        return next;
      });
      await fetchData();
      useToastStore.getState().showSuccess("Review Saved", "Statement review has been updated.");
    } catch (err: any) {
      const apiMsg = err?.responseBody?.errors?.[0]?.detail || err?.responseBody?.errors?.[0]?.title || err?.message;
      useToastStore.getState().showError("Error", apiMsg || "Failed to save review. Please try again.");
    } finally {
      setSavingDraft(false);
    }
  };

  const handleUnsaveAction = (execId: string) => {
    setSavedActions((prev) => {
      const next = new Set(prev);
      next.delete(execId);
      return next;
    });
  };

  /* ── Build flat rows for DataTable ── */
  let rowIdx = 0;
  const autoStatuses = controlStatuses.filter((s) => s.controlType === 'automated' && (s.opaResults?.length ?? 0) > 0);

  /* ── Compliance counts ── */
  /* Compliance score code intentionally disabled. */
  const satisfiedCount = 0;
  const totalItems = 0;
  const notSatisfiedCount = 0;
  const notCompletedCount = 0;
  const compliancePct = 0;
  const compliancePctColor = 'text-[#94a3b8] dark:text-white/60';

  // All review items from the API (both declarative and OPA) shown as declarative rows
  const declRows: TableRow[] = reviewItems.map((item) => {
    const ctrl = controls.find((c) => c.id === item.execution.planControlId);
    const stmtInfo = ctrl?.statements?.find(
      (s) => s.statementId === item.execution.statementId,
    );
    return { item, ctrl, stmtInfo, idx: rowIdx++, kind: "declarative" as const };
  });
  // Only add automation rows for controls NOT in the review API response
  const reviewControlIds = new Set(reviewItems.map((item) => item.execution.controlId));
  const autoRows: TableRow[] = autoStatuses
    .filter((s) => !reviewControlIds.has(s.controlId))
    .map((s) => ({
      autoCtrl: s, idx: rowIdx++, kind: "automation" as const,
    }));
  const rows: TableRow[] = [...declRows, ...autoRows];

  const failedAutoControlIds = autoStatuses
    .filter((s) => s.opaOverallStatus === "failed")
    .map((s) => s.controlId);

  const handleValidateWithLlm = async () => {
    if (!id || failedAutoControlIds.length === 0) return;
    setLlmValidating(true);
    try {
      const res = await validateControlsWithLlm(id, failedAutoControlIds);
      setLlmResults(res.data?.results ?? []);
      setLlmModalOpen(true);
    } catch (err: any) {
      const apiMsg = err?.responseBody?.errors?.[0]?.detail || err?.message;
      useToastStore.getState().showError("LLM Validation Failed", apiMsg || "Failed to validate controls with LLM.");
    } finally {
      setLlmValidating(false);
    }
  };

  const getCtrlId = (row: TableRow) => row.kind === 'declarative' ? (row.item.execution.controlId ?? '') : row.autoCtrl.controlId;
  const getCtrlTitle = (row: TableRow) => row.kind === 'declarative' ? (row.item.execution.controlDescription ?? '') : (row.autoCtrl.controlDescription ?? '');
  const getStmtText = (row: TableRow) => row.kind === 'declarative' ? (row.item.execution.statementDescription ?? '') : (row.autoCtrl.statements?.[0]?.statementDescription ?? '');

  /* ── Dynamic filter options from actual data ── */
  const getAvailableKinds = () => {
    const kinds = new Set<string>();
    rows.forEach(row => {
      if (row.kind === 'automation') {
        kinds.add('automation');
      } else {
        const methods = row.stmtInfo?.assessmentMethods ?? [];
        const isDE = row.item.execution.executionMethod === 'declarative_evidence' ||
                    (evidenceMap[row.item.execution.statementId]?.length ?? 0) > 0 ||
                    methods.includes('declarative_evidence');
        if (isDE) {
          kinds.add('declarative_evidence');
        } else {
          kinds.add('declarative');
        }
      }
    });
    return Array.from(kinds);
  };

  /* ── Row status helper ── */
  const getRowStatus = (row: TableRow): string => {
    if (row.kind === "automation") {
      const autoKey = `auto_${row.autoCtrl.controlId}`;
      const isAutoSaved = savedAutoActions.has(autoKey);
      if (isAutoSaved) {
        const action = autoActions[autoKey];
        if (action?.status === "accepted" || action?.status === "approved") return "saved_accepted";
        if (action?.status === "rejected") return "saved_rejected";
        return "saved_follow_up";
      }
      // Honor the review API status (follow-up resolved/raised, accepted/rejected)
      // just like declarative — the OPA pass/fail alone misses the review state.
      const autoRi = reviewItems.find((r) => r.execution.controlId === row.autoCtrl.controlId);
      const acrs = autoRi?.currentReviewStatus;
      if (acrs === "accepted" || acrs === "approved") return "approved";
      if (acrs === "rejected") return "rejected";
      if (acrs === "follow_up_resolved") return "follow_up_resolved";
      if (acrs === "follow_up_raised") return "follow_up_raised";
      return row.autoCtrl.opaOverallStatus === 'passed' ? "auto_passed" : "auto_failed";
    }
    const isSaved = savedActions.has(row.item.execution.id);
    if (isSaved) {
      const action = actions[row.item.execution.id];
      return (action?.status === "accepted" || action?.status === "approved")
        ? "saved_accepted"
        : action?.status === "rejected"
        ? "saved_rejected"
        : "saved_follow_up";
    }
    const crs = row.item.currentReviewStatus;
    if (crs === "accepted") return "approved";
    if (crs === "draft") {
      const history = row.item.reviewHistory ?? [];
      for (let i = history.length - 1; i >= 0; i--) {
        const entryStatus = getSavedActionStatus(history[i]);
        if (entryStatus === "rejected") return "saved_rejected";
        if (entryStatus === "follow_up_raised") return "saved_follow_up";
        if (entryStatus === "accepted") return "saved_accepted";
      }
      return "saved_accepted";
    }
    return crs;
  };

  // Label shown in the Action column button — mirrors the cell render below so
  // sorting groups actionable "Review" rows apart from completed "View" rows.
  const getActionLabel = (row: TableRow): "Review" | "View" => {
    if (row.kind === "automation") {
      const autoKey = `auto_${row.autoCtrl.controlId}`;
      if (savedAutoActions.has(autoKey)) return "View";
      const autoRi = reviewItems.find(
        (r) => r.execution.controlId === row.autoCtrl.controlId,
      );
      const acrs = autoRi?.currentReviewStatus;
      if (acrs === "accepted" || acrs === "approved" || acrs === "rejected")
        return "View";
      return "Review";
    }
    const status = row.item.currentReviewStatus;
    const isSaved = savedActions.has(row.item.execution.id);
    if (status === "accepted" || status === "approved" || status === "rejected")
      return "View";
    if (isSaved || status === "draft") return "View";
    if (status === "follow_up_raised") return "View";
    // follow_up_resolved and pending are still actionable → "Review"
    return "Review";
  };

  /* ── Dynamic filter options from actual data ── */
  const getAvailableStatuses = () => {
    const statuses = new Set<string>();
    rows.forEach(row => {
      const rs = getRowStatus(row);
      if (rs === "pending") statuses.add('not_reviewed');
      if (rs === "pending" || rs === "follow_up_raised") statuses.add('not_assessed');
      if (rs === "auto_failed" || rs === "saved_follow_up" || rs === "saved_rejected" || rs === "rejected") statuses.add('not_satisfied');
      if (rs === "approved" || rs === "saved_accepted") statuses.add('satisfied');
    });
    return Array.from(statuses);
  };

  const availableStatuses = getAvailableStatuses();

  const kindFilterOptions = [
    { value: "all", label: "All Types" },
    { value: "not_yet_assessed", label: "Not Yet Assessed" },
    { value: "declarative", label: "Declarative" },
    { value: "declarative_evidence", label: "Declarative + Evidence" },
    { value: "automation", label: "Automation" },
  ];

  const statusFilterOptions = [
    { value: "all", label: "All Status" },
    { value: "not_reviewed", label: "Not Reviewed", dot: "bg-slate-400" },
    { value: "not_assessed", label: "Not Assessed", dot: "bg-amber-500" },
    { value: "not_satisfied", label: "Not Satisfied", dot: "bg-red-500" },
    { value: "satisfied", label: "Satisfied", dot: "bg-emerald-500" },
  ];

  const filteredRows = rows.filter(row => {
    if (!tableSearch) return true;
    const q = tableSearch.toLowerCase();
    return getCtrlId(row).toLowerCase().includes(q) || getCtrlTitle(row).toLowerCase().includes(q) || getStmtText(row).toLowerCase().includes(q);
  });

  const SortIcon = ({ col }: { col: string }) => {
    const isActive = tableSortKey === col;
    return (
      <span className="inline-flex flex-col items-center ml-2 select-none" style={{ fontSize: '7px', lineHeight: '7px' }}>
        <span className={`font-black ${isActive && tableSortDir === 'asc' ? 'text-[#0f172a] dark:text-white' : 'text-[#0f172a]/40 dark:text-white/40'}`}>▲</span>
        <span className={`font-black ${isActive && tableSortDir === 'desc' ? 'text-[#0f172a] dark:text-white' : 'text-[#0f172a]/40 dark:text-white/40'}`}>▼</span>
      </span>
    );
  };

  const sortedRows = [...filteredRows].sort((a, b) => {
    if (!tableSortKey) return 0;
    let aVal: any, bVal: any;
    if (tableSortKey === 'idx') { return tableSortDir === 'asc' ? a.idx - b.idx : b.idx - a.idx; }
    else if (tableSortKey === 'control') { aVal = getCtrlId(a); bVal = getCtrlId(b); }
    else if (tableSortKey === 'title') { aVal = getCtrlTitle(a); bVal = getCtrlTitle(b); }
    else if (tableSortKey === 'assessmentMethod') {
      aVal = a.kind === 'automation' ? 'Test' : (a.item.execution.executionMethod || '');
      bVal = b.kind === 'automation' ? 'Test' : (b.item.execution.executionMethod || '');
    }
    else if (tableSortKey === 'testMethod') { aVal = a.kind; bVal = b.kind; }
    else if (tableSortKey === 'statement') { aVal = a.kind === 'automation' ? (a.autoCtrl.statements?.[0]?.statementDescription ?? '') : (a.item.execution.statementDescription ?? ''); bVal = b.kind === 'automation' ? (b.autoCtrl.statements?.[0]?.statementDescription ?? '') : (b.item.execution.statementDescription ?? ''); }
    else if (tableSortKey === 'implStatus') {
      aVal = (a.kind === 'automation' ? a.autoCtrl.implementationStatus : controlStatuses.find((s) => s.controlId === a.item.execution.controlId)?.implementationStatus) ?? '';
      bVal = (b.kind === 'automation' ? b.autoCtrl.implementationStatus : controlStatuses.find((s) => s.controlId === b.item.execution.controlId)?.implementationStatus) ?? '';
    }
    else if (tableSortKey === 'status') { aVal = getRowStatus(a); bVal = getRowStatus(b); }
    else if (tableSortKey === 'action') { aVal = getActionLabel(a); bVal = getActionLabel(b); }
    else return 0;
    const aStr = String(aVal ?? ''), bStr = String(bVal ?? '');
    return tableSortDir === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
  });

  /* ── Render ── */

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-3 py-24">
        <Loader2 className="size-5 animate-spin text-indigo-500 dark:text-indigo-400" />
        <span className="text-[13px] text-muted-foreground">Loading</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-24">
        <p className="text-[14px] text-[#94a3b8] dark:text-white/70">Something went wrong.</p>
        <button
          onClick={fetchData}
          className="px-4 py-2 rounded-lg border border-border text-sm hover:bg-accent"
        >
          Retry
        </button>
      </div>
    );
  }

  /* ── Automation action helpers ── */
  const updateAutoAction = (key: string, updates: Partial<ReviewAction>) => {
    setAutoActions((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] ?? { status: null, followUpQuestion: "", reviewComments: "", needsEvidence: false }),
        ...updates,
      },
    }));
  };

  const handleSaveAutoAction = async (key: string) => {
    if (!id) return;
    if (savingAuto) return; // guard against double-submit while in flight
    const action = autoActions[key];
    if (!action?.status) return;
    // Locate the statement for this automation control (execution-view → reviewItems).
    const ctrlId = key.replace(/^auto_/, "");
    const ri = reviewItems.find((r) => r.execution.controlId === ctrlId);
    if (!ri) {
      useToastStore.getState().showError("Error", "Could not locate the statement for this control.");
      return;
    }
    if (action.status === "follow_up_raised" && !action.followUpQuestion.trim()) {
      useToastStore.getState().showError("Follow-Up Note Required", "Please enter a follow-up note before saving.");
      return;
    }
    const payload =
      action.status === "follow_up_raised"
        ? {
            action: "followup" as const,
            needs_evidence: Boolean(action.needsEvidence),
            note: action.followUpQuestion.trim(),
          }
        : {
            action: automationVerdictAction(ctrlId, ri.execution.determination),
          };
    setSavingAuto(true);
    try {
      await reviewAssessmentPlanStatement(id, ri.execution.statementId, payload);
      setAutoActions((prev) => { const n = { ...prev }; delete n[key]; return n; });
      setSavedAutoActions((prev) => { const n = new Set(prev); n.delete(key); return n; });
      await fetchData();
      setAutoModalOpen(false);
      useToastStore.getState().showSuccess("Review Saved", "Statement review has been updated.");
    } catch (err: any) {
      const apiMsg = err?.responseBody?.errors?.[0]?.detail || err?.responseBody?.errors?.[0]?.title || err?.message;
      useToastStore.getState().showError("Error", apiMsg || "Failed to save review. Please try again.");
    } finally {
      setSavingAuto(false);
    }
  };

  const handleUnsaveAutoAction = (key: string) => {
    setSavedAutoActions((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  /* ── DataTable columns ── */
  const columns: Column<TableRow>[] = [
    {
      key: "idx",
      header: <span className="flex items-center justify-center">#</span>,
      align: "center",
      render: (row) => (
        <span className="text-[12px] text-[#64748b] dark:text-white/75 font-medium">
          {row.idx + 1}
        </span>
      ),
    },
    {
      key: "control",
      header: <button onClick={() => handleTableSort('control')} className="flex w-full items-center justify-center hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Control Id<SortIcon col="control" /></button>,
      align: "center",
      render: (row) => {
        const ctrlId =
          row.kind === "automation"
            ? row.autoCtrl.controlId
            : row.item.execution.controlId;
        return (
          <span className="px-2 py-0.5 rounded text-[12px] font-bold bg-indigo-50 dark:bg-violet-500/10 text-indigo-700 dark:text-violet-300 border border-indigo-200 dark:border-violet-500/20">
            {ctrlId?.toUpperCase()}
          </span>
        );
      },
    },
    {
      key: "title",
      header: <button onClick={() => handleTableSort('title')} className="flex w-full items-center justify-center hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Title<SortIcon col="title" /></button>,
      align: "left",
      ellipsis: 2,
      tooltip: (row) => row.kind === "automation"
        ? (row.autoCtrl.controlDescription || row.autoCtrl.controlId || "")
        : (row.item.execution.controlDescription || ""),
      render: (row) => {
        if (row.kind === "automation") {
          return (
            <p className="text-[12px] text-[#0f172a] dark:text-white/80 leading-relaxed break-words">
              {row.autoCtrl.controlDescription || row.autoCtrl.controlId?.toUpperCase() || "—"}
            </p>
          );
        }
        return (
          <p className="text-[12px] text-[#0f172a] dark:text-white/80 leading-relaxed break-words">
            {row.item.execution.controlDescription || "—"}
          </p>
        );
      },
    },
    {
      key: "statement",
      header: <button onClick={() => handleTableSort('statement')} className="flex w-full items-center justify-center hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Statement<SortIcon col="statement" /></button>,
      align: "left",
      ellipsis: 2,
      tooltip: (row) => {
        if (row.kind === "automation") {
          const firstStmt = row.autoCtrl.statements?.[0];
          return firstStmt?.statementDescription || "";
        }
        return row.item.execution.statementDescription || "";
      },
      render: (row) => {
        if (row.kind === "automation") {
          const opaCount = (row.autoCtrl.opaResults ?? []).length;
          const firstStmt = row.autoCtrl.statements?.[0];
          return (
            <div className="space-y-1">
              {firstStmt?.statementId && (
                <span className="inline-block text-[12px] font-mono font-bold text-indigo-600 dark:text-violet-400 bg-indigo-50 dark:bg-violet-500/10 px-1.5 py-0.5 rounded">
                  {firstStmt.statementId?.toUpperCase()}
                </span>
              )}
              <p className="text-[12px] text-[#475569] dark:text-white/60 leading-relaxed break-words">
                {firstStmt?.statementDescription || `${opaCount} automated check${opaCount !== 1 ? "s" : ""}`}
              </p>
            </div>
          );
        }
        return (
          <div className="space-y-1">
            <span className="inline-block text-[12px] font-mono font-bold text-indigo-600 dark:text-violet-400 bg-indigo-50 dark:bg-violet-500/10 px-1.5 py-0.5 rounded">
              {(row.item.execution.statementLabel || row.item.execution.statementId)?.toUpperCase()}
            </span>
            <p className="text-[12px] text-[#475569] dark:text-white/60 leading-relaxed break-words">
              {row.item.execution.statementDescription}
            </p>
          </div>
        );
      },
    },
    {
      key: "assessmentMethod",
      header: <button onClick={() => handleTableSort('assessmentMethod')} className="flex w-full items-center justify-center hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Assessment Method<SortIcon col="assessmentMethod" /></button>,
      align: "center",
      render: (row) => {
        if (row.kind === "automation" || row.item.execution.executionMethod === "opa") {
          return (
            <span className="inline-flex items-center justify-center w-[100px] py-1 rounded-full text-[12px] font-bold bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/20">
              Test
            </span>
          );
        }
        const methods = row.stmtInfo?.assessmentMethods ?? [];
        const assessMethods = methods.filter((m) => ["interview", "examine", "test"].includes(m));
        if (assessMethods.length === 0) {
          return (
            <span className="inline-flex items-center justify-center w-[100px] py-1 rounded-full text-[12px] font-bold bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20">
              Declarative
            </span>
          );
        }
        const colorMap: Record<string, string> = {
          interview: "bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-500/20",
          examine: "bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 border-indigo-200 dark:border-indigo-500/20",
          test: "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/20",
        };
        return (
          <div className="flex flex-wrap justify-center gap-1">
            {assessMethods.map((m) => (
              <span key={m} className={`inline-flex items-center justify-center w-[100px] py-1 rounded-full text-[12px] font-bold border ${colorMap[m] ?? "bg-slate-50 dark:bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-500/20"}`}>
                {m.charAt(0).toUpperCase() + m.slice(1)}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      key: "testMethod",
      header: <button onClick={() => handleTableSort('testMethod')} className="flex w-full items-center justify-center hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Test Method<SortIcon col="testMethod" /></button>,
      align: "center",
      render: (row) => {
        const indigoColor =
          "bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 border-indigo-200 dark:border-indigo-500/20";
        if (row.kind === "automation" || row.item.execution.executionMethod === "opa") {
          return (
            <span className={`inline-flex items-center justify-center w-[170px] py-1.5 rounded-full text-[12px] font-bold border whitespace-nowrap ${indigoColor}`}>
              <Zap className="size-3 inline mr-1" />Automation
            </span>
          );
        }
        // Trust the API: execution_method is the source of truth for the method
        // label. Don't promote to "Declarative + Evidence" just because evidence
        // happens to be linked (e.g. attached during a follow-up response).
        const label = row.item.execution.executionMethod === "declarative_evidence"
          ? "Declarative + Evidence"
          : "Declarative";
        return (
          <span className={`inline-flex items-center justify-center w-[170px] py-1.5 rounded-full text-[12px] font-bold border whitespace-nowrap ${indigoColor}`}>
            {label}
          </span>
        );
      },
    },
    {
      key: "action",
      header: <button onClick={() => handleTableSort('action')} className="flex w-full items-center justify-center hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Action<SortIcon col="action" /></button>,
      align: "center",
      render: (row) => {
        if (row.kind === "automation") {
          const autoKey = `auto_${row.autoCtrl.controlId}`;
          const isAutoSaved = savedAutoActions.has(autoKey);

          if (isAutoSaved) {
            const action = autoActions[autoKey];
            const isApproved = action?.status === "accepted" || action?.status === "approved";
            const isRejected = action?.status === "rejected";
            return (
              <button
                onClick={() => {
                  setAutoModalCtrl(row.autoCtrl);
                  setAutoModalOpen(true); setEvidencePage(0); setAutoOpaPage(0);
                }}
                className="inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 rounded-lg text-[12px] font-medium bg-indigo-600 dark:bg-indigo-600 text-white hover:opacity-90 transition-all"
              >
                {isApproved ? (
                  <Check className="size-3.5" />
                ) : isRejected ? (
                  <X className="size-3.5" />
                ) : null}
                View
              </button>
            );
          }

          // Reflect the review status like declarative: accepted/rejected → "View";
          // follow-up resolved → actionable "Review" with chat icon; else "Review".
          const autoRi = reviewItems.find((r) => r.execution.controlId === row.autoCtrl.controlId);
          const acrs = autoRi?.currentReviewStatus;
          const openAuto = () => {
            setAutoModalCtrl(row.autoCtrl);
            setAutoModalOpen(true); setEvidencePage(0); setAutoOpaPage(0);
          };
          if (acrs === "accepted" || acrs === "approved" || acrs === "rejected") {
            const isApproved = acrs === "accepted" || acrs === "approved";
            return (
              <button
                onClick={openAuto}
                className="inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 rounded-lg text-[12px] font-medium bg-indigo-600 dark:bg-indigo-600 text-white hover:opacity-90 transition-all"
              >
                {isApproved ? <Check className="size-3.5" /> : <X className="size-3.5" />}
                View
              </button>
            );
          }
          return (
            <button
              onClick={openAuto}
              className="inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 rounded-lg text-[12px] font-medium bg-indigo-600 dark:bg-indigo-600 text-white hover:opacity-90 transition-all"
            >
              {acrs === "follow_up_resolved" && <MessageSquare className="size-3.5" />}
              Review
            </button>
          );
        }

        const status = row.item.currentReviewStatus;
        const isSaved = savedActions.has(row.item.execution.id);

        if (status === "accepted" || status === "approved" || status === "rejected") {
          const isApproved = status === "accepted" || status === "approved";
          return (
            <button
              onClick={() => {
                setReviewModalRow(row);
                setReviewModalOpen(true);
              }}
              className="inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 rounded-lg text-[12px] font-medium bg-indigo-600 dark:bg-indigo-600 text-white hover:opacity-90 transition-all"
            >
              {isApproved ? (
                <Check className="size-3.5" />
              ) : (
                <X className="size-3.5" />
              )}
              View
            </button>
          );
        }

        if (isSaved || status === "draft") {
          const savedStatus = actions[row.item.execution.id]?.status;
          const isFollowUp = savedStatus === "follow_up_raised";
          return (
            <button
              onClick={() => {
                setReviewModalRow(row);
                setReviewModalOpen(true);
              }}
              className="inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 rounded-lg text-[12px] font-medium bg-indigo-600 dark:bg-indigo-600 text-white hover:opacity-90 transition-all"
            >
              {isFollowUp && <MessageSquare className="size-3.5" />}
              View
            </button>
          );
        }

        if (status === "follow_up_raised")
          return (
            <button
              onClick={() => {
                setReviewModalRow(row);
                setReviewModalOpen(true);
              }}
              className="inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 rounded-lg text-[12px] font-medium bg-indigo-600 dark:bg-indigo-600 text-white hover:opacity-90 transition-all"
            >
              <MessageSquare className="size-3.5" /> View
            </button>
          );

        if (status === "follow_up_resolved")
          // Owner re-submitted → the assessor still owes a decision, so this is an
          // actionable "Review" (becomes "View" once they approve/reject/follow-up).
          return (
            <button
              onClick={() => {
                setReviewModalRow(row);
                setReviewModalOpen(true);
              }}
              className="inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 rounded-lg text-[12px] font-medium bg-indigo-600 dark:bg-indigo-600 text-white hover:opacity-90 transition-all"
            >
              <MessageSquare className="size-3.5" /> Review
            </button>
          );

        return (
          <button
            onClick={() => {
              setReviewModalRow(row);
              setReviewModalOpen(true);
            }}
            className="inline-flex items-center whitespace-nowrap px-3 py-1.5 rounded-lg text-[12px] font-medium bg-indigo-600 dark:bg-indigo-600 text-white hover:opacity-90 transition-all"
          >
            Review
          </button>
        );
      },
    },
    {
      key: "status",
      header: <button onClick={() => handleTableSort('status')} className="flex w-full items-center justify-center hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Review Status<SortIcon col="status" /></button>,
      align: "center",
      render: (row) => {
        const status = getRowStatus(row);
        if (status === "auto_passed")
          return (
            <span className="inline-flex items-center justify-center gap-1 px-2 py-1 rounded text-[12px] font-bold bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20 whitespace-nowrap">
              <CheckCircle2 className="size-3.5" />
              Satisfied
            </span>
          );
        if (status === "auto_failed")
          return (
            <span className="inline-flex items-center justify-center gap-1 w-full px-2 py-1 rounded text-[12px] font-bold bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-500/20 whitespace-nowrap">
              <XCircle className="size-3.5" />
              Not-Satisfied
            </span>
          );
        if (status === "accepted" || status === "approved")
          return (
            <span className="inline-flex items-center justify-center gap-1 w-full px-2 py-1 rounded text-[12px] font-bold bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-500/20 whitespace-nowrap">
              <CheckCircle2 className="size-3.5" />
              Approved
            </span>
          );
        if (status === "saved_accepted")
          return (
            <span className="inline-flex items-center justify-center gap-1 w-full px-2 py-1 rounded text-[12px] font-bold bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-500/20 whitespace-nowrap">
              <CheckCircle2 className="size-3.5" />
              Approved
            </span>
          );
        if (status === "saved_follow_up")
          return (
            <span className="inline-flex items-center justify-center gap-1 w-full px-2 py-1 rounded text-[12px] font-bold bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-500/20 whitespace-nowrap">
              <MessageSquare className="size-3.5" />
              Follow-up Requested
            </span>
          );
        if (status === "follow_up_raised")
          return (
            <span className="inline-flex items-center justify-center gap-1 w-full px-2 py-1 rounded text-[12px] font-bold bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-500/20 whitespace-nowrap">
              <MessageSquare className="size-3.5" />
              Follow-up Requested
            </span>
          );
        if (status === "follow_up_resolved")
          return (
            <span className="inline-flex items-center justify-center gap-1 w-full px-2 py-1 rounded text-[12px] font-bold bg-teal-50 dark:bg-teal-500/10 text-teal-700 dark:text-teal-400 border border-teal-200 dark:border-teal-500/20 whitespace-nowrap">
              <CheckCircle2 className="size-3.5" />
              Follow-Up Resolved
            </span>
          );
        if (status === "rejected" || status === "saved_rejected")
          return (
            <span className="inline-flex items-center justify-center gap-1 w-full px-2 py-1 rounded text-[12px] font-bold bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-500/20 whitespace-nowrap">
              <XCircle className="size-3.5" />
              Rejected
            </span>
          );
        return (
          <span className="inline-flex items-center justify-center gap-1 w-full px-2 py-1 rounded text-[12px] font-bold bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/20 whitespace-nowrap">
            <Clock className="size-3.5" />
            Not Reviewed
          </span>
        );
      },
    },
  ];

  return (
    <PageShell className="space-y-6">
      <Breadcrumb
        parent="Assessment Review"
        parentTo="/continuous-compliance/assessment/assessment-review"
        current={planInfo ? (planInfo.title || "Assessment Review") : <span className="inline-block h-4 w-48 max-w-full animate-pulse rounded bg-foreground/10 align-middle" />}
        actions={
          // Show the Generate POA&M button only when the execution-view
          // reports `review_complete: true` (stored in `serverAllReviewed`).
          serverAllReviewed ? (
            <div className="flex items-center gap-2">
              <button
                onClick={handleGeneratePoam}
                disabled={generatingPoam}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-[12px] font-bold text-white bg-indigo-600 dark:bg-violet-600 hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {generatingPoam ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <ClipboardList className="size-3.5" />
                )}
                {generatingPoam ? "Generating" : "Generate POA&M"}
              </button>
            </div>
          ) : undefined
        }
      />

      {/* POA&M generated — choose where to go next */}
      <Modal
        open={showPoamDone}
        onClose={() => setShowPoamDone(false)}
        title="POA&M Generated"
        footer={
          <>
            <button
              onClick={() => navigate("/continuous-compliance/assessment/assessment-review")}
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors"
            >
              Go Back
            </button>
            <button
              onClick={() => id && navigate(`/continuous-compliance/assessment/assessment-result/${id}`)}
              className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-500/20"
            >
              View Results
            </button>
          </>
        }
      >
        <div className="flex gap-3">
          <div className="size-9 shrink-0 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center">
            <CheckCircle2 className="size-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="space-y-2 text-sm leading-relaxed text-[#475569] dark:text-white/70">
            <p>
              The POA&amp;M has been generated and this assessment is now finalized.
              The plan is no longer available on the Review and Execution pages.
            </p>
            <p>
              You can review its details on the Results page. Where would you like to
              go?
            </p>
          </div>
        </div>
      </Modal>

      {/* Plan Information */}
      {planInfo && (() => {
        const sspTitle = resolveSspTitle(planInfo.importSspHref) || planInfo.importSspHref || "-";
        const startDate = planInfo.task?.startDate ? new Date(planInfo.task.startDate).toLocaleDateString("en-GB") : "-";
        const endDate = planInfo.task?.endDate ? new Date(planInfo.task.endDate).toLocaleDateString("en-GB") : "-";
        const cards = [
          { label: "Plan Name", value: planInfo.title ?? "-", icon: ClipboardList, color: "text-indigo-500 dark:text-indigo-400", bg: "bg-indigo-50 dark:bg-indigo-500/10" },
          { label: "Linked SSP", value: sspTitle, icon: Link2, color: "text-sky-500 dark:text-sky-400", bg: "bg-sky-50 dark:bg-sky-500/10" },
          { label: "Start Date", value: startDate, icon: Calendar, color: "text-emerald-500 dark:text-emerald-400", bg: "bg-emerald-50 dark:bg-emerald-500/10" },
          { label: "End Date", value: endDate, icon: CalendarCheck, color: "text-amber-500 dark:text-amber-400", bg: "bg-amber-50 dark:bg-amber-500/10" },
        ];
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
            {cards.map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.label}
                  className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#131320] px-4 py-3 flex items-center gap-3 min-w-0"
                >
                  <div className={`size-9 rounded-lg flex items-center justify-center flex-shrink-0 ${c.bg}`}>
                    <Icon className={`size-4 ${c.color}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-medium text-[#94a3b8] dark:text-white/60">
                      {c.label}
                    </p>
                    <p
                      className="text-[13px] font-semibold text-[#0f172a] dark:text-white truncate"
                      title={String(c.value)}
                    >
                      {c.value}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Empty state */}
      {reviewItems.length === 0 && autoStatuses.length === 0 && (
        <div className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#131320] p-12 text-center mt-4">
          <div className="size-12 rounded-full bg-[#f1f5f9] dark:bg-white/[0.05] flex items-center justify-center mx-auto mb-3">
            <ShieldCheck className="size-5 text-[#cbd5e1] dark:text-white/75" />
          </div>
          <p className="text-sm font-medium text-[#475569] dark:text-white/50">
            No executions to review yet
          </p>
          <p className="text-[14px] text-[#94a3b8] dark:text-white/70 mt-1">
            Executions will appear here once the system owner submits them
          </p>
        </div>
      )}

      {/* Table */}
      <div className="relative flex flex-col flex-1 min-h-0 mt-8 gap-4">
        {/* Search + Kind filter toolbar */}
        {rows.length > 0 && (
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground/50 pointer-events-none" />
              <input
                type="text"
                placeholder="Search by control ID, title, or statement"
                value={tableSearch}
                onChange={e => { setTableSearch(e.target.value); setTablePage(0); }}
                className="w-full h-[34px] pl-8 pr-3 text-[13px] bg-[#f8fafc] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.06] rounded-lg outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-500 transition-all placeholder:text-muted-foreground/40"
              />
            </div>
            <div className="flex items-center gap-2 ml-auto flex-shrink-0">
              <label className="text-sm text-muted-foreground whitespace-nowrap">
                Rows per page
              </label>
              <Dropdown
                value={String(tablePageSize)}
                onChange={(v) => {
                  setTablePageSize(Number(v));
                  setTablePage(0);
                }}
                options={[10, 25, 50, 100].map((n) => ({ value: String(n), label: String(n) }))}
                size="sm"
                align="end"
                className="!min-w-[80px]"
              />
            </div>
          </div>
        )}

        {/* DataTable */}
        {rows.length > 0 && (
          <div className="flex-1 min-h-0">
            <DataTable
              data={sortedRows.slice(tablePage * tablePageSize, (tablePage + 1) * tablePageSize)}
              columns={columns}
              page={tablePage}
              onPageChange={setTablePage}
              pageSize={tablePageSize}
              totalItems={sortedRows.length}
              minTableWidth="1100px"
              emptyText={rows.length === 0 ? 'No executions to review yet' : 'No controls match your search'}
            />
          </div>
        )}
      </div>


      {/* ══════ REVIEW MODAL ══════ */}
      {reviewModalOpen &&
        reviewModalRow &&
        (() => {
          const row = reviewModalRow;
          const exec = row.item.execution;
          const action = actions[exec.id] ?? {
            status: null,
            followUpQuestion: "",
            reviewComments: "",
            needsEvidence: false,
          };
          const isSaved = savedActions.has(exec.id);
          // Show Assessor Action (Approve / Raise Follow-Up / Reject) for any
          // statement that isn't already finalized. REVIEWED → accepted/rejected
          // stays locked; everything else (new, awaiting review, follow-up
          // resolved) is actionable.
          const canAct =
            row.item.currentReviewStatus !== "accepted" &&
            row.item.currentReviewStatus !== "approved" &&
            row.item.currentReviewStatus !== "rejected";
          // While a follow-up is raised the ball is in the system owner's court —
          // the assessor can't approve/reject/re-raise until the owner responds
          // (state moves FOLLOWUP → FOLLOWUP_RESOLVED). Until then we only show
          // the raised follow-up in the footer, not the action buttons.
          const awaitingOwnerResponse =
            getRowStatus(row) === "follow_up_raised" ||
            getRowStatus(row) === "saved_follow_up";
          // A statement saved as plain "declarative" carries no evidence.
          // Ignore any evidence links that linger from an abandoned
          // "declarative + evidence" attempt so the modal matches the Test
          // Method column (which keys off executionMethod).
          const isDeclarativeOnly = exec.executionMethod === "declarative";
          const stmtEvidence = isDeclarativeOnly
            ? []
            : evidenceMap[exec.statementId] ?? [];
          const hasEvidence = stmtEvidence.length > 0;
          // Look up this statement's iterations so we can split evidence
          // by round: anything linked BEFORE iter[1].saved_at is the
          // original submission's evidence; anything at/after is the
          // owner's follow-up evidence. Backend doesn't tag evidence
          // per-iteration, so we use the link's createdAt vs the second
          // iteration's saved_at as a heuristic split. With only one
          // iteration, all evidence is treated as original.
          let stmtIterationsForSplit: AssessmentExecutionViewIteration[] = [];
          for (const ctrl of controls) {
            const stmt = ctrl.statements.find(
              (s) => s.id === exec.statementId || s.statementId === exec.statementId,
            );
            if (stmt) {
              stmtIterationsForSplit = stmt.iterations ?? [];
              break;
            }
          }
          const followUpCutoffTs = stmtIterationsForSplit[1]?.saved_at ?? null;
          const sortedEvidence = [...stmtEvidence].sort((a, b) =>
            (a.createdAt ?? "").localeCompare(b.createdAt ?? ""),
          );
          const originalEvidence = followUpCutoffTs
            ? sortedEvidence.filter((ev) => (ev.createdAt ?? "") < followUpCutoffTs)
            : sortedEvidence;
          const followUpEvidence = followUpCutoffTs
            ? sortedEvidence.filter((ev) => (ev.createdAt ?? "") >= followUpCutoffTs)
            : [];
          const isOpa = exec.executionMethod === "opa";
          const opaDetail = isOpa ? controlStatuses.find((s) => s.controlId === exec.controlId) : null;
          const methodLabel = isOpa
            ? "Automation (OPA)"
            : hasEvidence
              ? "Declarative + Evidence"
              : "Declarative";
          const hasExecutionResponse =
            Boolean(exec.justification?.trim()) || hasEvidence;
          // Show the follow-up panel only when there's an actual follow-up conversation,
          // not just the initial justification.
          const isFollowUpResolved =
            row.item.currentReviewStatus === "follow_up_resolved";

          return (
            <ModalShell
              open
              onClose={() => {
                setReviewModalOpen(false);
                setReviewOpaPage(0);
                setShowExecutionResponse(false);
              }}
              variant="wide"
            >
                {/* Modal Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0] dark:border-white/[0.06] flex-shrink-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="px-2 py-0.5 rounded text-[12px] font-bold bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-500/20">
                      {exec.controlId?.toUpperCase()}
                    </span>
                    <span className="text-[15px] font-medium text-[#0f172a] dark:text-white">
                      {(exec.statementLabel || exec.statementId)?.toUpperCase()}
                    </span>
                    {(() => {
                      const rs = getRowStatus(row);
                      // Automated (OPA) controls have an objective pass/fail result up front.
                      // Declarative items only have a satisfaction state AFTER the assessor
                      // has reviewed them — `exec.determination` is the SYSTEM OWNER's
                      // declaration, not the review outcome, so we must not use it here.
                      if (isOpa) {
                        const satisfied = opaDetail?.opaOverallStatus === "passed";
                        return (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px] font-bold ${
                            satisfied
                              ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                              : "bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400"
                          }`}>
                            {satisfied ? <CheckCircle2 className="size-3" /> : <XCircle className="size-3" />}
                            {satisfied ? "Satisfied" : "Not Satisfied"}
                          </span>
                        );
                      }
                      const reviewed =
                        rs === "approved" ||
                        rs === "saved_accepted" ||
                        rs === "rejected" ||
                        rs === "saved_rejected" ||
                        rs === "saved_follow_up" ||
                        rs === "follow_up_raised" ||
                        rs === "follow_up_resolved" ||
                        rs === "auto_failed";
                      if (!reviewed) {
                        return (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px] font-bold bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400">
                            Pending Review
                          </span>
                        );
                      }
                      if (rs === "saved_follow_up" || rs === "follow_up_raised") {
                        return (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px] font-bold bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400">
                            Follow-up Requested
                          </span>
                        );
                      }
                      if (rs === "follow_up_resolved") {
                        return (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px] font-bold bg-teal-50 dark:bg-teal-500/10 text-teal-700 dark:text-teal-400">
                            Follow-Up Resolved
                          </span>
                        );
                      }
                      const satisfied = rs === "approved" || rs === "saved_accepted";
                      return (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px] font-bold ${
                          satisfied
                            ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                            : "bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400"
                        }`}>
                          {satisfied ? <CheckCircle2 className="size-3" /> : <XCircle className="size-3" />}
                          {satisfied ? "Satisfied" : "Not Satisfied"}
                        </span>
                      );
                    })()}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => {
                        setReviewModalOpen(false);
                        setShowExecutionResponse(false);
                      }}
                      className="p-1.5 rounded-lg hover:bg-[#f1f5f9] dark:hover:bg-white/[0.06] transition-colors"
                    >
                      <X className="size-5 text-[#64748b] dark:text-white/40" />
                    </button>
                  </div>
                </div>

                {/* Modal Body — px-5 matches the AutomationChecksView toolbar's
                    -mx-5 so the sticky toolbar's background extends edge-to-edge
                    and content scrolls cleanly behind it (mirrors Execution). */}
                <div className="flex-1 min-h-0 overflow-y-auto px-5 py-5 space-y-5">
                  {/* Statement description */}
                  <div className="p-3 rounded-lg bg-[#f8fafc] dark:bg-white/[0.02] border border-[#e2e8f0] dark:border-white/[0.06]">
                    <p className="text-[12px] text-[#475569] dark:text-white/60 leading-relaxed">
                      {exec.statementDescription}
                    </p>
                  </div>

                  {/* Standalone Justification — always shown for declarative
                      and D+E. Top-level `justification` is the system owner's
                      ORIGINAL submission. The amber follow-up panel below
                      carries only the follow-up Q&A. */}
                  {!isOpa && (
                    <div>
                      <p className="text-[12px] font-bold text-[#0f172a] dark:text-white mb-1.5">
                        Justification
                      </p>
                      <div className="rounded-lg border border-[#e2e8f0] dark:border-white/[0.06] bg-[#f8fafc] dark:bg-white/[0.02] p-3">
                        <p className="text-[12px] text-[#334155] dark:text-white/70 whitespace-pre-wrap break-words leading-relaxed">
                          {exec.justification?.trim() || (
                            <span className="text-[#94a3b8] italic">No justification provided.</span>
                          )}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Standalone Evidence — only the ORIGINAL submission's
                      evidence (linked before iter 2 was saved). The
                      follow-up answer's evidence renders inside the
                      amber follow-up panel below to avoid duplication. */}
                  {!isOpa && originalEvidence.length > 0 && (
                    <div>
                      <p className="text-[12px] font-bold text-[#0f172a] dark:text-white mb-1.5">
                        Evidence ({originalEvidence.length})
                      </p>
                      <div className="space-y-2">
                        {originalEvidence.map((ev) => (
                          <div
                            key={ev.id}
                            className="flex items-center justify-between gap-3 rounded-lg border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-white/[0.02] px-3 py-2"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <FileText className="size-4 text-indigo-500 dark:text-violet-400 flex-shrink-0" />
                              <p className="text-[12px] font-medium text-[#0f172a] dark:text-white truncate">
                                {ev.evidenceName || `Evidence ${ev.supportingEvidenceId.slice(0, 8)}`}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button
                                onClick={() =>
                                  handleEvidencePreview(
                                    ev.supportingEvidenceId,
                                    ev.evidenceName || `Evidence ${ev.supportingEvidenceId.slice(0, 8)}`,
                                  )
                                }
                                className="p-1.5 rounded-lg hover:bg-[#e2e8f0] dark:hover:bg-white/[0.06] transition-colors"
                                title="Preview"
                              >
                                <Eye className="size-3.5 text-[#475569] dark:text-white/60" />
                              </button>
                              <button
                                onClick={() =>
                                  handleEvidenceDownload(
                                    ev.supportingEvidenceId,
                                    ev.evidenceName ?? undefined,
                                  )
                                }
                                className="p-1.5 rounded-lg hover:bg-[#e2e8f0] dark:hover:bg-white/[0.06] transition-colors"
                                title="Download"
                              >
                                <Download className="size-3.5 text-[#475569] dark:text-white/60" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Follow-up panel (assessor variant) — follow-up Q&A only.
                      ownerResponse = latest iteration's justification (the SO's
                      actual follow-up reply, not the original justification).
                      No evidence here — original evidence renders in the
                      standalone card above. */}
                  {isFollowUpResolved && (() => {
                    const latestHist = [...(row.item.reviewHistory ?? [])]
                      .reverse()
                      .find((h) => h.followUpQuestion || h.followUpResponse);
                    const chatCount = (row.item.reviewHistory ?? []).filter(
                      (h) => h.followUpQuestion || h.followUpResponse,
                    ).length;
                    const fuControlType: FollowUpControlType = isOpa
                      ? "automation"
                      : hasEvidence
                        ? "declarative_evidence"
                        : "declarative";
                    // Latest iteration's justification is the SO's follow-up
                    // answer. Reuse the iterations already looked up above
                    // for the evidence split.
                    const stmtIterations = stmtIterationsForSplit;
                    const latestIter = stmtIterations[stmtIterations.length - 1] ?? null;
                    return (
                      <FollowUpPanel
                        role="assessor"
                        status="resolved"
                        controlType={fuControlType}
                        controlId={row.ctrl?.controlId ?? exec.planControlId}
                        statementLabel={exec.statementId}
                        assessorQuestion={
                          latestHist?.followUpQuestion ?? row.item.execution.followUpQuestion ?? null
                        }
                        ownerResponse={latestIter?.justification ?? null}
                        // Follow-up evidence = links created at/after the
                        // second iteration's saved_at. Renders inside the
                        // panel so the assessor sees the owner's response
                        // text AND the new evidence together.
                        evidence={followUpEvidence.map((ev) => ({
                          id: ev.id,
                          name:
                            ev.evidenceName ||
                            `Evidence ${ev.supportingEvidenceId.slice(0, 8)}`,
                          onPreview: () =>
                            handleEvidencePreview(
                              ev.supportingEvidenceId,
                              ev.evidenceName ||
                                `Evidence ${ev.supportingEvidenceId.slice(0, 8)}`,
                            ),
                          onDownload: () =>
                            handleEvidenceDownload(
                              ev.supportingEvidenceId,
                              ev.evidenceName ?? undefined,
                            ),
                        }))}
                        chatCount={chatCount}
                        onOpenChat={
                          chatCount > 0
                            ? () => {
                                setReviewModalOpen(false);
                                openChat(row.item, row.ctrl?.controlId ?? exec.planControlId);
                              }
                            : undefined
                        }
                        defaultExpanded={showExecutionResponse}
                      />
                    );
                  })()}

                  {/* OPA Evidence (for automation items) - paginated to keep
                      the modal compact when there are many OPA results. */}
                  {isOpa && opaDetail && (
                    <AutomationChecksView control={opaDetail} />
                  )}

                </div>

                {/* ── Sticky decision footer (always visible) ── */}
                <div className="flex-shrink-0 border-t border-[#e2e8f0] dark:border-white/[0.06] px-6 py-4 bg-[#f8fafc] dark:bg-white/[0.02] max-h-[50vh] overflow-y-auto space-y-3">
                  {/* Follow-up raised — waiting for system owner response.
                      While awaiting, the assessor has no action; show only the
                      raised follow-up + chat. Approve/Reject/Follow-Up return
                      once the owner responds (FOLLOWUP_RESOLVED). */}
                  {awaitingOwnerResponse && !isSaved && (() => {
                    const latestIter = stmtIterationsForSplit[stmtIterationsForSplit.length - 1] ?? null;
                    const latestReviewerNote = latestIter?.reviewer_note ?? null;
                    return (
                      <div className="p-3.5 rounded-lg bg-amber-50/50 dark:bg-amber-500/[0.06] border border-amber-200 dark:border-amber-500/20">
                        <p className="text-[13px] font-bold text-amber-800 dark:text-amber-300">
                          Follow-Up Raised
                        </p>
                        {(row.item.execution.followUpQuestion || action.followUpQuestion || latestReviewerNote) && (
                          <div className="flex flex-col gap-1 mt-2 text-left">
                            {(row.item.execution.followUpQuestion || action.followUpQuestion) && (
                              <p className="text-xs text-amber-900 dark:text-amber-200 leading-relaxed">
                                &ldquo;{row.item.execution.followUpQuestion || action.followUpQuestion}&rdquo;
                              </p>
                            )}
                            {latestReviewerNote && (
                              <p className="text-xs text-amber-900 dark:text-amber-200 leading-relaxed">
                                {latestReviewerNote}
                              </p>
                            )}
                          </div>
                        )}
                        {row.item.reviewHistory.length > 0 && (
                          <div className="flex justify-end mt-3">
                            <button
                              onClick={() => {
                                setReviewModalOpen(false);
                                openChat(
                                  row.item,
                                  row.ctrl?.controlId ?? exec.planControlId,
                                );
                              }}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium text-[#475569] dark:text-white/70 bg-[#f1f5f9] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.06] hover:bg-[#e2e8f0] dark:hover:bg-white/[0.08] transition-colors"
                            >
                              <MessageSquare className="size-3.5" />
                              Chat
                              <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-indigo-600 text-white text-[10px] font-bold leading-none">
                                {row.item.reviewHistory.filter((h) => h.followUpQuestion || h.followUpResponse).length}
                              </span>
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Action buttons */}
                  {canAct && !isSaved && !awaitingOwnerResponse && (
                    <div className="space-y-3">
                      <p className="text-[13px] font-semibold text-[#cbd5e1] dark:text-white/80">
                        Assessor Action
                      </p>
                      <div className="flex gap-2">
                        <RBACAction module="assessment_review" action="approve">
                          <button
                            onClick={() =>
                              updateAction(exec.id, {
                                status:
                                  action.status === "approved"
                                    ? null
                                    : "approved",
                                followUpQuestion: "",
                              })
                            }
                            className={`flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-[12px] font-medium transition-all ${
                              action.status === "approved"
                                ? "bg-emerald-600 text-white shadow-sm shadow-emerald-500/20 ring-2 ring-emerald-400/50"
                                : "bg-emerald-700 text-white border border-emerald-600 hover:bg-emerald-600"
                            }`}
                          >
                            {action.status === "approved" && <Check className="size-3.5" />} Accept
                          </button>
                        </RBACAction>
                        {/* Raise Follow-Up hidden for automation (OPA) controls */}
                        {!isOpa && (
                          <button
                            onClick={() =>
                              updateAction(exec.id, {
                                status:
                                  action.status === "follow_up_raised"
                                    ? null
                                    : "follow_up_raised",
                              })
                            }
                            className={`flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-[12px] font-medium transition-all ${
                              action.status === "follow_up_raised"
                                ? "bg-amber-600 text-white shadow-sm shadow-amber-500/20 ring-2 ring-amber-400/50"
                                : "bg-amber-700 text-white border border-amber-600 hover:bg-amber-600"
                            }`}
                          >
                            {action.status === "follow_up_raised" && <Check className="size-3.5" />} Raise Follow-Up
                          </button>
                        )}
                        
                        {!isOpa && (
                          <RBACAction module="assessment_review" action="approve">
                            <button
                              onClick={() =>
                                updateAction(exec.id, {
                                  status:
                                    action.status === "rejected"
                                      ? null
                                      : "rejected",
                                  followUpQuestion: "",
                                })
                              }
                              className={`flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-[12px] font-medium transition-all ${
                                action.status === "rejected"
                                  ? "bg-red-600 text-white shadow-sm shadow-red-500/20 ring-2 ring-red-400/50"
                                  : "bg-red-700 text-white border border-red-600 hover:bg-red-600"
                              }`}
                            >
                              {action.status === "rejected" && <Check className="size-3.5" />} Reject
                            </button>
                          </RBACAction>
                        )}
                      </div>

                      {/* Review comments input (for approve/reject) */}
                      {(action.status === "approved" || action.status === "rejected") && (
                        <div className="space-y-2">
                          <textarea
                            placeholder={
                              action.status === "rejected"
                                ? "Add rejection comments (optional)"
                                : "Add review comments (optional)"
                            }
                            value={action.reviewComments ?? ""}
                            onChange={(e) =>
                              updateAction(exec.id, {
                                reviewComments: e.target.value,
                              })
                            }
                            rows={2}
                            className={`w-full text-xs bg-white dark:bg-white/[0.04] rounded-lg px-3 py-2.5 outline-none border transition-all placeholder:text-[#94a3b8] dark:placeholder:text-white/60 resize-none text-[#0f172a] dark:text-white max-h-[140px] overflow-y-auto ${
                              action.status === "rejected"
                                ? "border-red-200 dark:border-red-500/20 focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                                : "border-emerald-200 dark:border-emerald-500/20 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                            }`}
                          />
                        </div>
                      )}

                      {/* Follow-up question input */}
                      {action.status === "follow_up_raised" && (
                        <div className="space-y-2">
                          <textarea
                            placeholder="Enter your follow-up note"
                            value={action.followUpQuestion}
                            onChange={(e) =>
                              updateAction(exec.id, {
                                followUpQuestion: e.target.value,
                              })
                            }
                            rows={5}
                            className="w-full text-xs bg-white dark:bg-white/[0.04] rounded-lg px-3 py-2.5 outline-none border border-amber-200 dark:border-amber-500/20 focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all placeholder:text-[#94a3b8] dark:placeholder:text-white/60 resize-none text-[#0f172a] dark:text-white max-h-[220px] overflow-y-auto"
                          />
                          <div className="flex items-center justify-between">
                            <p
                              className={`text-[12px] ${action.followUpQuestion.trim() ? "text-emerald-500" : "text-[#cbd5e1] dark:text-white/75"}`}
                            >
                              {action.followUpQuestion.trim() ? "Ready to send" : "Note is required"}
                            </p>
                            <label className="flex items-center gap-1.5 text-[12px] text-amber-700 dark:text-amber-400 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={action.needsEvidence ?? false}
                                onChange={() =>
                                  updateAction(exec.id, {
                                    needsEvidence: !(
                                      action.needsEvidence ?? false
                                    ),
                                  })
                                }
                                className="size-3.5 rounded border-amber-300 text-amber-600 focus:ring-amber-500/20"
                              />
                              Request Evidence
                            </label>
                          </div>
                        </div>
                      )}

                      {/* Save button */}
                      {action.status && (
                        <div className="flex justify-end">
                          <RBACAction module="assessment_result" action="approve">
                            <button
                              onClick={async () => {
                                await handleSaveAction(exec.id);
                                setReviewModalOpen(false);
                              }}
                              disabled={
                                savingDraft ||
                                (action.status === "follow_up_raised" &&
                                !action.followUpQuestion.trim())
                              }
                              className="flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-[13px] font-bold bg-indigo-600 dark:bg-indigo-600 text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                            >
                              {savingDraft && <Loader2 className="size-3.5 animate-spin" />}
                              {savingDraft ? "Saving" : "Save"}
                            </button>
                          </RBACAction>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Saved state - show what was saved with Edit option */}
                  {canAct && isSaved && (
                    <div className="space-y-3 pt-2 border-t border-[#e2e8f0] dark:border-white/[0.06]">
                      <div className="flex items-center justify-between p-2.5 rounded-lg bg-indigo-50/50 dark:bg-indigo-500/[0.06] border border-indigo-200 dark:border-indigo-500/20">
                        <p className="text-[12px] font-bold text-indigo-700 dark:text-indigo-400 flex items-center gap-1.5">
                          <CheckCircle2 className="size-3.5" />
                          {(action.status === "accepted" || action.status === "approved")
                            ? "Approved"
                            : action.status === "rejected"
                            ? "Rejected"
                            : "Follow-Up Raised"}
                          {action.status === "follow_up_raised" &&
                            action.followUpQuestion && (
                              <span className="font-normal text-indigo-600/70 dark:text-indigo-400/70 ml-1">
                                - "{action.followUpQuestion}"
                              </span>
                            )}
                        </p>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {action.status === "follow_up_raised" && (
                            <button
                              onClick={() => {
                                setReviewModalOpen(false);
                                openChat(
                                  row.item,
                                  row.ctrl?.controlId ?? exec.planControlId,
                                );
                              }}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium text-[#475569] dark:text-white/70 bg-[#f1f5f9] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.06] hover:bg-[#e2e8f0] dark:hover:bg-white/[0.08] transition-colors"
                            >
                              <MessageSquare className="size-3.5" />
                              Chat
                              <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-indigo-600 text-white text-[10px] font-bold leading-none">
                                {row.item.reviewHistory.filter((h) => h.followUpQuestion || h.followUpResponse).length}
                              </span>
                            </button>
                          )}
                          <button
                            onClick={() => handleUnsaveAction(exec.id)}
                            className="text-[12px] font-bold text-indigo-600 dark:text-indigo-400 hover:underline"
                          >
                            Edit
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Already approved read-only */}
                  {(row.item.currentReviewStatus === "accepted" || row.item.currentReviewStatus === "approved") && (
                    <div className="p-2.5 rounded-lg bg-emerald-50/50 dark:bg-emerald-500/[0.06] border border-emerald-200 dark:border-emerald-500/20 flex items-center">
                      <p className="text-[12px] font-bold text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5">
                        <CheckCircle2 className="size-3.5" /> This statement has
                        been approved
                      </p>
                    </div>
                  )}

                  {/* Already rejected read-only (server-confirmed, not local save) */}
                  {row.item.currentReviewStatus === "rejected" && !isSaved && (
                    <div className="p-2.5 rounded-lg bg-red-50/50 dark:bg-red-500/[0.06] border border-red-200 dark:border-red-500/20 flex items-center">
                      <p className="text-[12px] font-bold text-red-700 dark:text-red-400 flex items-center gap-1.5">
                        <CheckCircle2 className="size-3.5" /> This statement has been rejected
                      </p>
                    </div>
                  )}

                  {/* Close button */}
                  {/* <div className="flex justify-end pt-2">
                  <button onClick={() => setReviewModalOpen(false)} className="px-5 py-2 rounded-lg text-[13px] font-bold bg-[#f1f5f9] dark:bg-white/[0.06] text-[#475569] dark:text-white/50 hover:bg-[#e2e8f0] dark:hover:bg-white/[0.08] transition-colors">
                    Close
                  </button>
                </div> */}
                </div>
            </ModalShell>
          );
        })()}

      {/* ══════ AUTOMATION REVIEW MODAL ══════ */}
      {autoModalOpen && autoModalCtrl && (() => {
        const autoKey = `auto_${autoModalCtrl.controlId}`;
        const autoAction = autoActions[autoKey] ?? { status: null, followUpQuestion: "", reviewComments: "", needsEvidence: false };
        const isAutoSaved = savedAutoActions.has(autoKey);
        const autoWordCount = countWords(autoAction.followUpQuestion);
        // Link to the execution-view statement so we can show the latest follow-up
        // message + open the chat history (mirrors the Declarative / D+E flow).
        const autoRi = reviewItems.find((r) => r.execution.controlId === autoModalCtrl.controlId) ?? null;
        const autoRiStatus = autoRi?.currentReviewStatus;
        const autoLatestMsg = autoRi?.execution.followUpQuestion || null;
        const autoChatCount = (autoRi?.reviewHistory ?? []).filter((h) => h.followUpQuestion || h.followUpResponse).length;
        const autoFollowUpActive = autoRiStatus === "follow_up_raised" || autoRiStatus === "follow_up_resolved";
        // Follow-up raised → the ball is in the system owner's court. Hide the
        // assessor actions (Approve / Reject / Raise Follow-Up) until the owner
        // responds (FOLLOWUP → FOLLOWUP_RESOLVED); only the raised follow-up +
        // chat are shown meanwhile.
        const autoAwaitingOwner = autoRiStatus === "follow_up_raised";
        // System owner's re-submitted justification + linked evidence for this
        // control (shown to the reviewer once a follow-up has been resolved).
        const autoStmt =
          (autoModalCtrl.executionViewStatements ?? []).find((s) => (s.state ?? "").toUpperCase() === "FOLLOWUP_RESOLVED")
          ?? (autoModalCtrl.executionViewStatements ?? []).find((s) => (s.iterations?.length ?? 0) > 0 || (s.evidence_files?.length ?? 0) > 0)
          ?? (autoModalCtrl.executionViewStatements ?? [])[0]
          ?? null;
        // Owner's response is ALWAYS the latest iteration's justification — never
        // the outer statement.justification (which mirrors a single iteration and
        // is ambiguous across multi-round follow-ups per the new iterations flow).
        const autoLatestIter = autoStmt?.iterations?.[autoStmt.iterations.length - 1] ?? null;
        const autoOwnerJustification = autoLatestIter?.justification?.trim() || null;
        // Evidence links may be keyed by the statement UUID or its text label
        // (the owner links via the label), so check both.
        const autoOwnerEvidence = autoStmt
          ? (evidenceMap[autoStmt.statement_id] ?? evidenceMap[autoStmt.statement_label] ?? [])
          : [];

        return (
          <ModalShell open onClose={() => { if (savingAuto) return; setAutoModalOpen(false); setAutoRespViewOpen(false); }} closable={!savingAuto} zIndexClass="z-[55]" variant="wide">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-[#e2e8f0] dark:border-white/[0.06] bg-[#f8fafc] dark:bg-white/[0.02] flex-shrink-0">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-mono font-bold text-cyan-600 dark:text-cyan-400 bg-cyan-50 dark:bg-cyan-500/10 px-2.5 py-1 rounded-lg">
                    {autoModalCtrl.controlId?.toUpperCase()}
                  </span>
                  <span className="text-[14px] font-medium text-[#0f172a] dark:text-white">
                    Automation Results
                  </span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px] font-bold ${
                    autoModalCtrl.opaOverallStatus === 'passed'
                      ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                      : "bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400"
                  }`}>
                    {autoModalCtrl.opaOverallStatus === 'passed' ? <CheckCircle2 className="size-3" /> : <XCircle className="size-3" />}
                    {autoModalCtrl.opaOverallStatus === 'passed' ? "Satisfied" : "Not Satisfied"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { if (savingAuto) return; setAutoModalOpen(false); setAutoRespViewOpen(false); }}
                    disabled={savingAuto}
                    className="size-8 rounded-lg flex items-center justify-center hover:bg-[#e2e8f0] dark:hover:bg-white/[0.06] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <X className="size-4 text-[#64748b]" />
                  </button>
                </div>
              </div>

             
              <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-5 flex flex-col gap-3">
                {autoModalCtrl.controlDescription && (
                  <p className="order-1 text-[13px] text-[#334155] dark:text-white/80 leading-relaxed pt-5">{autoModalCtrl.controlDescription}</p>
                )}

                <div className="order-3">
                  <AutomationChecksView control={autoModalCtrl} />
                </div>

                {/* Unified follow-up panel (assessor variant, automation) —
                    replaces both the System Owner Response card and the
                    Follow-Up status bar with a single collapsed/expanded UI. */}
                {autoFollowUpActive && (
                  <div className="order-2">
                    <FollowUpPanel
                      role="assessor"
                      status={autoRiStatus === "follow_up_resolved" ? "resolved" : "pending"}
                      controlType="automation"
                      controlId={autoModalCtrl.controlId}
                      statementLabel={autoRi?.execution.statementId}
                      assessorQuestion={autoLatestMsg}
                      ownerResponse={autoOwnerJustification}
                      evidence={autoOwnerEvidence.map((ev) => ({
                        id: ev.id,
                        name:
                          ev.evidenceName || `Evidence ${ev.supportingEvidenceId.slice(0, 8)}`,
                        onPreview: () =>
                          handleEvidencePreview(
                            ev.supportingEvidenceId,
                            ev.evidenceName ||
                              `Evidence ${ev.supportingEvidenceId.slice(0, 8)}`,
                          ),
                        onDownload: () =>
                          handleEvidenceDownload(
                            ev.supportingEvidenceId,
                            ev.evidenceName ?? undefined,
                          ),
                      }))}
                      chatCount={autoChatCount}
                      onOpenChat={
                        autoRi && autoChatCount > 0
                          ? () => {
                              setAutoModalOpen(false);
                              openChat(autoRi, autoModalCtrl.controlId);
                            }
                          : undefined
                      }
                      defaultExpanded={autoRespViewOpen}
                    />
                  </div>
                )}

                {/* ── Accept / Follow-Up actions for automation ── */}
                {!isAutoSaved && (
                  <div className="space-y-3 pt-2 border-t border-[#e2e8f0] dark:border-white/[0.06]">
                    <p className="text-[13px] font-semibold text-[#cbd5e1] dark:text-white/80">
                      Assessor Action
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() =>
                          updateAutoAction(autoKey, {
                            status: autoAction.status === "approved" ? null : "approved",
                            followUpQuestion: "",
                          })
                        }
                        className={`flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-[12px] font-medium transition-all ${
                          autoAction.status === "approved"
                            ? "bg-emerald-600 text-white shadow-sm shadow-emerald-500/20 ring-2 ring-emerald-400/50"
                            : "bg-emerald-700 text-white border border-emerald-600 hover:bg-emerald-600"
                        }`}
                      >
                        {autoAction.status === "approved" && <Check className="size-3.5" />} Approve
                      </button>
                     
                    </div>

                    {/* Review comments input (for approve) */}
                    {autoAction.status === "approved" && (
                      <div className="space-y-2">
                        <textarea
                          placeholder="Add review comments (optional)"
                          value={autoAction.reviewComments ?? ""}
                          onChange={(e) =>
                            updateAutoAction(autoKey, { reviewComments: e.target.value })
                          }
                          rows={2}
                          className="w-full text-xs bg-white dark:bg-white/[0.04] rounded-lg px-3 py-2.5 outline-none border border-emerald-200 dark:border-emerald-500/20 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all placeholder:text-[#94a3b8] dark:placeholder:text-white/60 resize-none text-[#0f172a] dark:text-white max-h-[140px] overflow-y-auto"
                        />
                      </div>
                    )}

                    {/* Follow-up question input */}
                    {autoAction.status === "follow_up_raised" && (
                      <div className="space-y-2">
                        <textarea
                          placeholder="Enter your follow-up question (minimum 20 words)"
                          value={autoAction.followUpQuestion}
                          onChange={(e) =>
                            updateAutoAction(autoKey, { followUpQuestion: e.target.value })
                          }
                          rows={5}
                          className="w-full text-xs bg-white dark:bg-white/[0.04] rounded-lg px-3 py-2.5 outline-none border border-amber-200 dark:border-amber-500/20 focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all placeholder:text-[#94a3b8] dark:placeholder:text-white/60 resize-none text-[#0f172a] dark:text-white max-h-[220px] overflow-y-auto"
                        />
                        <div className="flex items-center justify-between">
                          <p className={`text-[12px] ${autoWordCount >= MIN_WORDS ? "text-emerald-500" : "text-[#cbd5e1] dark:text-white/75"}`}>
                            {autoWordCount}/{MIN_WORDS} words
                          </p>
                          <label className="flex items-center gap-1.5 text-[12px] text-amber-700 dark:text-amber-400 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={autoAction.needsEvidence ?? false}
                              onChange={() =>
                                updateAutoAction(autoKey, { needsEvidence: !(autoAction.needsEvidence ?? false) })
                              }
                              className="size-3.5 rounded border-amber-300 text-amber-600 focus:ring-amber-500/20"
                            />
                            Request Evidence
                          </label>
                        </div>
                      </div>
                    )}

                    {/* Save button */}
                    {autoAction.status && (
                      <div className="flex justify-end">
                        <button
                          onClick={() => handleSaveAutoAction(autoKey)}
                          disabled={
                            savingAuto ||
                            (autoAction.status === "follow_up_raised" && autoWordCount < MIN_WORDS)
                          }
                          className="flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-[13px] font-bold bg-indigo-600 dark:bg-violet-600 text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                        >
                          {savingAuto && <Loader2 className="size-3.5 animate-spin" />}
                          {savingAuto ? "Saving" : "Save"}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Saved state */}
                {isAutoSaved && (
                  <div className="space-y-3 pt-2 border-t border-[#e2e8f0] dark:border-white/[0.06]">
                    <div className="flex items-center justify-between p-2.5 rounded-lg bg-indigo-50/50 dark:bg-indigo-500/[0.06] border border-indigo-200 dark:border-indigo-500/20">
                      <p className="text-[12px] font-bold text-indigo-700 dark:text-indigo-400 flex items-center gap-1.5">
                        <CheckCircle2 className="size-3.5" />
                        {(autoAction.status === "accepted" || autoAction.status === "approved")
                          ? "Approved"
                          : autoAction.status === "rejected"
                          ? "Rejected"
                          : "Follow-Up Raised"}
                        {autoAction.status === "follow_up_raised" && autoAction.followUpQuestion && (
                          <span className="font-normal text-indigo-600/70 dark:text-indigo-400/70 ml-1">
                            — "{autoAction.followUpQuestion}"
                          </span>
                        )}
                      </p>
                      <button
                        onClick={() => handleUnsaveAutoAction(autoKey)}
                        className="text-[12px] font-bold text-indigo-600 dark:text-indigo-400 hover:underline"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                )}
              </div>
          </ModalShell>
        );
      })()}

      {/* ── Follow-Up Chat Modal ── */}
      {chatOpen && chatItem && (() => {
        // Look up the full iteration timeline from controls (kept in state
        // after fetchData). Falls back to [] for statements without history.
        let chatIterations: AssessmentExecutionViewIteration[] = [];
        for (const ctrl of controls) {
          const stmt = ctrl.statements.find(
            (s) => s.id === chatItem.execution.statementId || s.statementId === chatItem.execution.statementId,
          );
          if (stmt) {
            chatIterations = stmt.iterations ?? [];
            break;
          }
        }
        return (
        <FollowUpChatModal
          open={chatOpen}
          onClose={() => setChatOpen(false)}
          onBack={reviewModalRow ? () => {
            setChatOpen(false);
            setReviewModalOpen(true);
          } : undefined}
          statementId={chatItem.execution.statementId}
          controlId={chatControlId}
          reviewHistory={buildChatHistory(
            reviewHistoryMap[chatItem.execution.statementLabel ?? ""] ??
              reviewHistoryMap[chatItem.execution.statementId] ??
              reviewHistoryMap[chatItem.execution.id] ??
              chatItem.reviewHistory,
            chatItem.execution,
            chatItem.currentReviewStatus,
          )}
          iterations={chatIterations}
          currentFollowUpQuestion={chatItem.execution.followUpQuestion}
          currentStatus={chatItem.currentReviewStatus}
          perspective="review"
          onSendFollowUp={id ? async (text: string) => {
            await reviewAssessmentPlanStatement(id, chatItem.execution.statementId, {
              action: "followup",
              needs_evidence: false,
              note: text,
            });
            await fetchData();
          } : undefined}
        />
        );
      })()}

      {/* ── LLM Validation Results Modal ── */}
      {llmModalOpen && llmResults && (
        <ModalShell open onClose={() => setLlmModalOpen(false)} zIndexClass="z-[60]">
          <div className="flex items-center justify-between px-5 py-3 border-b border-[#e2e8f0] dark:border-white/[0.06] bg-[#f8fafc] dark:bg-white/[0.02] flex-shrink-0">
            <div className="flex items-center gap-2.5">
              <Zap className="size-4 text-violet-500 dark:text-violet-400" />
              <span className="text-[14px] font-semibold text-[#0f172a] dark:text-white">LLM Validation Results</span>
              <span className="text-[11px] text-[#64748b] dark:text-white/40 bg-[#f1f5f9] dark:bg-white/[0.04] px-2 py-0.5 rounded">
                {llmResults.length} control{llmResults.length !== 1 ? "s" : ""}
              </span>
            </div>
            <button
              onClick={() => setLlmModalOpen(false)}
              className="size-8 rounded-lg flex items-center justify-center hover:bg-[#e2e8f0] dark:hover:bg-white/[0.06] transition-colors"
            >
              <X className="size-4 text-[#64748b]" />
            </button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-3">
            {llmResults.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-[13px] text-[#94a3b8] dark:text-white/40">No results returned from LLM.</p>
              </div>
            ) : (
              llmResults.map((result) => {
                const isPassed = result.status === "pass";
                const isPartial = result.status === "partial";
                const statusColor = isPassed
                  ? "border-emerald-100 dark:border-emerald-500/10 bg-emerald-50/40 dark:bg-emerald-500/[0.04]"
                  : isPartial
                  ? "border-amber-100 dark:border-amber-500/10 bg-amber-50/40 dark:bg-amber-500/[0.04]"
                  : "border-red-100 dark:border-red-500/10 bg-red-50/40 dark:bg-red-500/[0.04]";
                const badgeColor = isPassed
                  ? "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                  : isPartial
                  ? "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400"
                  : "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-400";
                const BadgeIcon = isPassed ? CheckCircle2 : isPartial ? AlertCircle : XCircle;
                const statusLabel = isPassed ? "Pass" : isPartial ? "Partial" : "Fail";
                return (
                  <div key={result.controlId} className={`rounded-xl border p-4 space-y-3 ${statusColor}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="px-2 py-0.5 rounded text-[12px] font-bold bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-500/20">
                        {result.controlId?.toUpperCase()}
                      </span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[12px] font-bold ${badgeColor}`}>
                        <BadgeIcon className="size-3" />
                        {statusLabel}
                      </span>
                      <span className="ml-auto text-[11px] text-[#64748b] dark:text-white/40">
                        Confidence: {Math.round(result.confidence * 100)}%
                      </span>
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold text-[#64748b] dark:text-white/40 tracking-wide mb-1">Reasoning</p>
                      <p className="text-[12px] text-[#475569] dark:text-white/60 leading-relaxed">{result.reasoning}</p>
                    </div>
                    {result.recommendations.length > 0 && (
                      <div>
                        <p className="text-[11px] font-semibold text-[#64748b] dark:text-white/40 tracking-wide mb-1.5">Recommendations</p>
                        <ul className="space-y-1">
                          {result.recommendations.map((rec, i) => (
                            <li key={i} className="flex items-start gap-1.5 text-[12px] text-[#475569] dark:text-white/60">
                              <span className="mt-1.5 size-1.5 rounded-full bg-violet-400 dark:bg-violet-500 flex-shrink-0" />
                              {rec}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </ModalShell>
      )}

      {/* ── Evidence Preview Modal ── */}
      {previewEvidenceId && (
        <ModalShell open onClose={closePreview} zIndexClass="z-[60]" variant="preview">
          <FilePreviewHeader
            title={previewTitle}
            fileName={null}
            downloadUrl={downloadSupportingEvidenceFile(previewEvidenceId)}
            onClose={closePreview}
          />
          <FilePreviewBody
            url={previewSupportingEvidenceFile(previewEvidenceId)}
            fileName={null}
          />
        </ModalShell>
      )}
    </PageShell>
  );
}
